// src/components/HomeTabs.jsx
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Collapse,
  Grid,
  List,
  ListItemButton,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import BacktestAreaChart from "./BacktestAreaChart";
import BacktestBarChart from "./BacktestBarChart";
import LiveAreaChart from "./LiveAreaChart";
import MethodologyText from "./MethodologyText";
import RecentForecastTable from "./RecentForecastTable";
import StatsTables from "./StatsTables";

import "./HomeTabs.css"; // <= page-specific CSS

const TabPanel = ({ value, current, children }) =>
  String(value) === String(current) ? <>{children}</> : null;

const PAGE_LIMIT = 1000;

const isTrue = (value) => value === true || value === 'true' || value === 1;

function HomeTabs(props) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const [tabType, setTabType] = useState("backtest");
  const [selectedTab, setSelectedTab] = useState(null); // string id
  const [openCollapseId, setOpenCollapseId] = useState(null); // portfolio id
  const [portFolioList, setPortFolioList] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- data ---
  const fetchPortfolios = async () => {
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/portfolios/?skip=0&limit=${PAGE_LIMIT}`
      );
      if (r.status === 200) return r.data.data;
    } catch (e) {
      console.error(e);
    }
    return [];
  };
  const fetchAssets = async () => {
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/assets?skip=0&limit=${PAGE_LIMIT}`
      );
      if (r.status === 200) return r.data.data;
    } catch (e) {
      console.error(e);
    }
    return [];
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [pf, as] = await Promise.all([fetchPortfolios(), fetchAssets()]);
      const list = pf.map((p) => ({
        ...p,
        subcategories: as.filter((s) => s.portfolio_id === p.id),
      }));
      setPortFolioList(list);
      if (list[0]) {
        setSelectedAssets(list[0]);
        setSelectedTab(String(list[0].id));
        setOpenCollapseId(list[0].id);
      }
      setLoading(false);
    })();
  }, []);

  // --- handlers ---
  const handleTabsChange = (_e, newVal) => {
    const tabId = String(newVal);
    const foundP = portFolioList.find((p) => String(p.id) === tabId);
    const foundS = portFolioList
      .flatMap((p) => p.subcategories || [])
      .find((s) => String(s.id) === tabId);
    const found = foundP || foundS;
    if (!found) return;
    setSelectedTab(String(found.id));
    setSelectedAssets(found);
    const parent = foundP
      ? foundP
      : portFolioList.find((p) =>
          (p.subcategories || []).some(
            (s) => String(s.id) === String(found.id)
          )
        );
    if (parent) setOpenCollapseId(parent.id);
  };

  const onPortfolioClick = useCallback((item) => {
    if (!item?.visible || item?.disabled) return;
    setSelectedTab(String(item.id));
    setSelectedAssets(item);
    setOpenCollapseId((prev) => (prev === item.id ? null : item.id));
  }, []);

  const onSubAssetClick = useCallback(
    (doc) => {
      setSelectedTab(String(doc.id));
      setSelectedAssets(doc);
      const parent = portFolioList.find((p) =>
        (p.subcategories || []).some((s) => String(s.id) === String(doc.id))
      );
      if (parent) setOpenCollapseId(parent.id);
    },
    [portFolioList]
  );

  const ToggleBar = useMemo(
    () => (
      <div className="ht-toggleRow">
        {["Live Forecasts", "backtest", "methodology"].map((t) => (
          <span
            key={t}
            onClick={() => setTabType(t)}
            className={`ht-pill ${tabType === t ? "is-active" : ""}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </span>
        ))}
      </div>
    ),
    [tabType]
  );

  return (
    <>
      {loading ? (
        <div className="ht-loaderWrap">
          <ClipLoader size={45} color="#005687" />
        </div>
      ) : (
        <div className="ht-wrap">
          {!isSmall && portFolioList.length > 0 && ToggleBar}
          <Grid container spacing={3} mt={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              {/* LEFT: sidebar */}
              <div className="ht-sidebar">
                <Tabs
                  orientation="vertical"
                  value={selectedTab || false}
                  onChange={handleTabsChange}
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  className="ht-tabs"
                >
                  <List className="ht-list">
                    {portFolioList.map((item) => {
                      if (!item?.visible) return null;
                      const open = openCollapseId === item.id;
                      const active = selectedTab === String(item.id);
                      return (
                        <div key={item.id} className="ht-block">
                          {/* portfolio card */}
                          <Tab
                            value={String(item.id)}
                            disableRipple
                            disabled={!!item.disabled}
                            onClick={() => onPortfolioClick(item)}
                            className={`ht-card ${active ? 'is-active' : ''} ${
                              item.disabled ? 'is-disabled' : ''
                            }`}
                            label={
                              <div className="ht-cardInner">
                                <div className="ht-logo">
                                  {/* left square logo */}
                                  {item.icon_url ||
                                  item.cover_image ||
                                  item.cover_image_url ? (
                                    <img
                                      src={`${process.env.REACT_APP_BASE_URL}${
                                        item.icon_url ||
                                        item.cover_image ||
                                        item.cover_image_url
                                      }`}
                                      alt={item.name}
                                    />
                                  ) : (
                                    <span className="ht-logoFallback">MQE</span>
                                  )}
                                </div>
                                <div className="ht-title">{item?.name}</div>
                                <div className="ht-chevron">
                                  {open ? (
                                    <ExpandLess className="ht-chevronIcon" />
                                  ) : (
                                    <ExpandMore className="ht-chevronIcon" />
                                  )}
                                </div>
                              </div>
                            }
                          />
                          {/* subassets */}
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            {(item.subcategories || [])
                              .filter((d) => d?.visible)
                              .map((doc) => {
                                const subActive =
                                  selectedTab === String(doc.id);
                                return (
                                  <ListItemButton
                                    key={doc.id}
                                    disableRipple
                                    className="ht-subItemWrap"
                                    onClick={() => onSubAssetClick(doc)}
                                  >
                                    <Tab
                                      value={String(doc.id)}
                                      disableRipple
                                      className={`ht-subCard ${
                                        subActive ? 'is-active' : ''
                                      }`}
                                      label={
                                        <div className="ht-subInner">
                                          <div className="ht-subThumb">
                                            {doc.logo ? (
                                              <img
                                                src={`${process.env.REACT_APP_BASE_URL}${doc.logo}`}
                                                alt={doc.name}
                                              />
                                            ) : (
                                              <span className="ht-logoFallback small">
                                                FX
                                              </span>
                                            )}
                                          </div>
                                          <div className="ht-subTitle">
                                            {doc.name}
                                          </div>
                                        </div>
                                      }
                                    />
                                  </ListItemButton>
                                );
                              })}
                          </Collapse>
                        </div>
                      );
                    })}
                  </List>
                </Tabs>
              </div>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              {/* RIGHT: content */}
              <div className="ht-content">
                {isSmall && portFolioList.length > 0 && ToggleBar}

                {selectedAssets && (
                  <TabPanel value={selectedAssets.id} current={selectedTab}>
                    {tabType === 'backtest' ? (
                      <>
                        <BacktestAreaChart
                          theme={props.theme}
                          assetName={selectedAssets?.name}
                          id={selectedAssets?.id}
                        />
                        <BacktestBarChart
                          metricName="Sharpe"
                          data={selectedAssets}
                          theme={props.theme}
                        />
                        <h3 className="color-ucla mt-4">
                          Evaluation Metrics of MQE Algorithm on{' '}
                          {selectedAssets?.name}
                        </h3>
                        <StatsTables data={selectedAssets} />
                      </>
                    ) : tabType === 'Live Forecasts' ? (
                      <>
                        <h3
                          className="color-ucla"
                          style={{
                            textAlign: 'center',
                            marginBottom: 10,
                            marginTop: 15,
                          }}
                        >
                          Past Forecast Performance of Trading Strategy
                        </h3>
                        <LiveAreaChart
                          theme={props.theme}
                          assetName={selectedAssets?.name}
                          id={selectedAssets?.id}
                          data={selectedAssets}
                          resourceType={
                            selectedAssets?.subcategories
                              ? 'portfolio'
                              : 'assets'
                          }
                        />
                        {(!selectedAssets?.subcategories ||
                          isTrue(selectedAssets?.is_carry_trade)) && (
                          <>
                            <h3 className="color-ucla">
                              Performance of Recent Forecasts
                            </h3>
                            <RecentForecastTable
                              id={selectedAssets?.id}
                              asset={selectedAssets}
                              resourceType={
                                selectedAssets?.subcategories
                                  ? 'portfolio'
                                  : 'assets'
                              }
                              forecastTerm={props.forecastTerm}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <MethodologyText data={selectedAssets} />
                    )}
                  </TabPanel>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
}

export default HomeTabs;
